import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useSession } from '@/lib/auth-client';
import { trpcClient } from '@/lib/trpc';

interface AdminModeContextType {
  isAdminMode: boolean;
  impersonatedOrgId: string | null;
  impersonatedOrgName: string | null;
  enterAdminMode: (orgId: string, orgName: string) => void;
  exitAdminMode: () => void;
  canUseAdminMode: boolean;
}

const AdminModeContext = createContext<AdminModeContextType | undefined>(undefined);

const ADMIN_MODE_STORAGE_KEY = 'screenata_admin_mode';

interface AdminModeData {
  orgId: string;
  orgName: string;
  timestamp: number;
}

export function AdminModeProvider({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [impersonatedOrgId, setImpersonatedOrgId] = useState<string | null>(null);
  const [impersonatedOrgName, setImpersonatedOrgName] = useState<string | null>(null);

  // Capture URL params ONCE on mount before any routing
  const urlParamsRef = useRef<{ orgId: string; orgName: string } | null>(null);
  if (urlParamsRef.current === null) {
    const params = new URLSearchParams(window.location.search);
    const adminModeParam = params.get('admin_mode');
    const orgIdParam = params.get('org_id');
    const orgNameParam = params.get('org_name');

    if (adminModeParam === 'true' && orgIdParam && orgNameParam) {
      urlParamsRef.current = {
        orgId: orgIdParam,
        orgName: decodeURIComponent(orgNameParam),
      };
    }
  }

  // Check if user is SITE_ADMIN
  const canUseAdminMode = session?.user?.role === 'SITE_ADMIN';

  // Load admin mode from storage on mount
  useEffect(() => {
    // Wait for session to load before checking permissions
    if (isPending) {
      return;
    }

    if (!canUseAdminMode) {
      // Clear admin mode if user is not SITE_ADMIN (only after session loads)
      localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
      return;
    }

    const stored = localStorage.getItem(ADMIN_MODE_STORAGE_KEY);

    if (stored) {
      try {
        const data: AdminModeData = JSON.parse(stored);

        // Check if admin mode session is still valid (24 hours)
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        const age = Date.now() - data.timestamp;

        if (age < maxAge) {
          setIsAdminMode(true);
          setImpersonatedOrgId(data.orgId);
          setImpersonatedOrgName(data.orgName);
        } else {
          // Expired - clear it
          localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
        }
      } catch {
        localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
      }
    }
  }, [canUseAdminMode, isPending]);

  // Define logAdminAccess first so it can be used in enterAdminMode
  const logAdminAccess = useCallback(async (orgId: string, orgName: string, action: 'enter' | 'exit') => {
    try {
      // Use tRPC instead of fetch to avoid proxy issues
      await trpcClient.admin.logAdminAccess.mutate({
        action: `admin_mode_${action}`,
        resourceType: 'organization',
        resourceId: orgId,
        metadata: {
          organizationName: orgName,
          userId: session?.user?.id,
          userEmail: session?.user?.email,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        },
      });
    } catch {
      // Silently fail - audit logging is non-critical
    }
  }, [session]);

  const enterAdminMode = useCallback(async (orgId: string, orgName: string) => {
    if (!canUseAdminMode) {
      return;
    }

    try {
      // Store admin mode UI state in localStorage
      const data: AdminModeData = {
        orgId,
        orgName,
        timestamp: Date.now(),
      };

      localStorage.setItem(ADMIN_MODE_STORAGE_KEY, JSON.stringify(data));

      setIsAdminMode(true);
      setImpersonatedOrgId(orgId);
      setImpersonatedOrgName(orgName);

      // Log admin access for audit trail
      await logAdminAccess(orgId, orgName, 'enter');

      // Set the target organization as the SITE_ADMIN's active org
      // This makes the SITE_ADMIN "become" a user of that org
      await trpcClient.organizations.setActiveOrganization.mutate({
        organizationId: orgId
      });

      // Fetch the organization's workspaces
      const result = await trpcClient.organizations.getOrganizationWithWorkspaces.query({ id: orgId });

      if (result.organization?.workspaces?.[0]) {
        const firstWorkspace = result.organization.workspaces[0];
        window.location.href = `/workspaces/${firstWorkspace.id}`;
      } else {
        // No workspaces, go to home
        window.location.href = '/';
      }
    } catch {
      // Clear localStorage on failure
      localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
    }
  }, [canUseAdminMode, logAdminAccess]);

  // Check for admin mode params captured on mount
  useEffect(() => {
    // Wait for session to load
    if (isPending) {
      return;
    }

    if (!canUseAdminMode) {
      // Clear captured params if user is not SITE_ADMIN
      urlParamsRef.current = null;
      return;
    }

    // Don't process URL params if already in admin mode
    if (isAdminMode) {
      return;
    }

    // Check captured params from mount
    if (urlParamsRef.current) {
      // Enter admin mode
      enterAdminMode(urlParamsRef.current.orgId, urlParamsRef.current.orgName);

      // Clear the ref so we don't process again
      urlParamsRef.current = null;
    }
  }, [isPending, canUseAdminMode, isAdminMode, enterAdminMode]);

  const exitAdminMode = async () => {
    try {
      // Log exit for audit trail
      if (impersonatedOrgId && impersonatedOrgName) {
        await logAdminAccess(impersonatedOrgId, impersonatedOrgName, 'exit');
      }

      // Clear all admin mode storage
      localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
      localStorage.removeItem('partner:delegation'); // Also clear partner delegation if present
      localStorage.removeItem('auth:activeOrg');
      localStorage.removeItem('auth:activeWorkspace');
      localStorage.removeItem('auth:activeWorkspaceId');

      setIsAdminMode(false);
      setImpersonatedOrgId(null);
      setImpersonatedOrgName(null);

      // Navigate to admin panel
      window.location.href = import.meta.env.VITE_ADMIN_URL || 'http://localhost:5174';
    } catch {
      // Still clear storage and navigate on error
      localStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
      localStorage.removeItem('partner:delegation');
      localStorage.removeItem('auth:activeOrg');
      localStorage.removeItem('auth:activeWorkspace');
      localStorage.removeItem('auth:activeWorkspaceId');
      window.location.href = import.meta.env.VITE_ADMIN_URL || 'http://localhost:5174';
    }
  };

  const value: AdminModeContextType = {
    isAdminMode,
    impersonatedOrgId,
    impersonatedOrgName,
    enterAdminMode,
    exitAdminMode,
    canUseAdminMode,
  };

  return (
    <AdminModeContext.Provider value={value}>
      {children}
    </AdminModeContext.Provider>
  );
}

export function useAdminMode() {
  const context = useContext(AdminModeContext);
  if (context === undefined) {
    throw new Error('useAdminMode must be used within AdminModeProvider');
  }
  return context;
}
